import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { Button, ToastRegion } from '@amumin/ui';

import { EmptyState } from '../lib/EmptyState.js';
import { rememberPairingLocation } from '../lib/pairingResume.js';
import { LinkButton } from '../lib/ui.js';
import { appText, authState, context, logout, messages, refreshContext } from '../state/session.js';
import { BrandLogo } from './BrandLogo.js';
import { Shell } from './Shell.js';

/** Het accountscherm werkt ook zonder huishouden (sessies, wachtwoord, account verwijderen). */
const ACCOUNT_PATH = '/instellingen/beveiliging';

/**
 * Alleen met een geldige fb_sess; anders naar /login. Laadt de context één keer per app-start.
 * `bare`: zonder zijbalk en onderbalk, alleen de logo-regel (de wizard: één vraag per stap, geen
 * dertien uitgangen eromheen; polish w4 ronde 2).
 *
 * Ronde 1 golf 8 (S12): een login zonder enig huishouden (een meekijker die uit de kring is
 * gehaald of zelf stopte) krijgt geen gezinsschil met foutmeldingen, maar een rustige pagina met
 * alleen Account en Uitloggen.
 */
export function Protected({
  children,
  bare,
}: {
  children: ComponentChildren;
  bare?: boolean | undefined;
}) {
  const { route, path } = useLocation();
  const state = authState.value;

  useEffect(() => {
    if (state === 'unknown') refreshContext().catch(() => undefined);
  }, [state]);

  useEffect(() => {
    if (state === 'anonymous') {
      rememberPairingLocation();
      route('/login', true);
    }
  }, [state, route]);

  const ctx = context.value;
  const orphan =
    state === 'authenticated' &&
    ctx !== null &&
    ctx.households.length === 0 &&
    ctx.platform_admin !== true;

  if (state === 'authenticated' && (bare || orphan)) {
    const c = appText.value.care;
    return (
      <div class="bare-shell">
        <p class="auth-brand">
          <BrandLogo label={messages.value.web.appName} />
        </p>
        <main id="main" class="bare-main">
          {orphan && path !== ACCOUNT_PATH ? (
            <EmptyState
              title={c.orphanTitle}
              body={c.orphanBody}
              heading="h1"
              name="no-household"
              action={
                <div class="wizard-actions">
                  <LinkButton href={ACCOUNT_PATH} icon="user" name="no-household-account">
                    {c.orphanAccount}
                  </LinkButton>
                  <Button name="no-household-logout" variant="ghost" onPress={() => void logout()}>
                    {c.orphanLogout}
                  </Button>
                </div>
              }
            />
          ) : (
            children
          )}
        </main>
        <ToastRegion dismissLabel={messages.value.design.toast.dismiss} />
      </div>
    );
  }
  if (state === 'authenticated') return <Shell>{children}</Shell>;
  return <p class="page-loading">{messages.value.web.loading}</p>;
}
